/**
 * Superscript/subscript sizing and baseline offset (WMPCM-10141).
 *
 * `TextRun.verticalAlign` (`api/types/stowed-element.ts`) is canvas-model-only — see that field's
 * own doc comment for why there is no wire representation to write it into. This module is the one
 * place that turns it into an actual size/position, shared by measurement
 * (`shape-text-measure.ts`) and rendering (`shape-text-editor/components/shape-text-chunk.tsx`) on purpose: this codebase has
 * hit "the thing that measures and the thing that draws disagreed" repeatedly (`larry-frontend-ui/CLAUDE.md`
 * Known Gaps #18, #38, #42), and its own anti-pattern table names the fix — "measure with the
 * engine that draws". A superscript/subscript character painted smaller than the box it was
 * measured into leaves a visible gap after it; routing both sides through `effectivePointSizeForRun`
 * is what rules that out by construction.
 */

import type { TextRun } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'

/** Superscript/subscript size as a fraction of the run's own (unscaled) point size — the common
 *  typographic convention browsers use for `<sup>`/`<sub>` (roughly 0.6-0.7em). */
export const VERTICAL_ALIGN_SIZE_SCALE = 0.65

/** Baseline shift as a fraction of the run's own (unscaled) point size — up for superscript, down
 *  for subscript. Independent of `VERTICAL_ALIGN_SIZE_SCALE`: the shift is measured against the
 *  full-size em box, not the shrunk glyph, matching how a real `<sup>`/`<sub>` renders. */
export const VERTICAL_ALIGN_OFFSET_SCALE = 0.35

/**
 * The point size a run actually measures and paints at. Identical to `run?.pointSize ?? fallback`
 * whenever `verticalAlign` is unset — a strict no-op for every run this feature doesn't touch, which
 * is the regression-safety property every other text feature sharing this measurement path relies
 * on (row height, leading, caret position, chain distribution).
 */
export function effectivePointSizeForRun(
  run: TextRun | null | undefined,
  fallback: number = DEFAULT_POINT_SIZE
): number {
  const base = run?.pointSize ?? fallback

  return run?.verticalAlign ? base * VERTICAL_ALIGN_SIZE_SCALE : base
}

/**
 * Vertical pixel offset to paint a superscript/subscript run's glyph at, relative to its normal
 * baseline position — negative (up) for `'super'`, positive (down) for `'sub'`, `0` for anything
 * else. `basePointSizePx` is the run's own UNSCALED point size (document points == CSS px in this
 * editor, see `canvas-font-metrics.ts`) — the shift is a fraction of the full em box, not the
 * already-shrunk `effectivePointSizeForRun` result.
 */
export function verticalAlignOffsetPx(
  run: TextRun | null | undefined,
  basePointSizePx: number
): number {
  if (run?.verticalAlign === 'super') return -basePointSizePx * VERTICAL_ALIGN_OFFSET_SCALE
  if (run?.verticalAlign === 'sub') return basePointSizePx * VERTICAL_ALIGN_OFFSET_SCALE

  return 0
}
